'use client';
import { ChevronRight } from 'lucide-react';
import Image from 'next/image';
import React, { useEffect, useMemo, useState, Suspense } from 'react';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import Button from '@/components/ui/Button';
import '@/styles/user-auth-modal.css';
import { logoutSession } from '@/features/auth';
import { setOpenLoginModal, useAuthStore } from '@/stores';
import { useAuthMe } from '@/features/user';
import MenuToggle from '@/components/ui/MenuToggle';
import { ZANGIA_LOGOS } from '@/lib/utils/constants';
import { cn } from '@/lib/utils/utils';
import { isChristmasPeriod } from '@/lib/utils/dateUtils';
import ModalWrapper from '../ui/ModalWrapper';
import Modal from '@/components/ui/Modal';
import { AnimatePresence, motion } from 'framer-motion';
import { UserDTO } from '@/shared';
import {
  clearPendingAuthRedirect,
  getCurrentPath,
  takePendingAuthRedirect,
} from '@/lib/auth/pending-auth-redirect';

const NavigationDropdownForUser = React.lazy(
  () => import('./NavigationDropdownForUser')
);
const NavigationDropdownNotification = React.lazy(
  () => import('./NavigationDropdownNotification')
);
const UserActionToolbar = React.lazy(
  () => import('@/components/layouts/UserActionToolbar')
);
const ProductToolbar = React.lazy(
  () => import('@/components/layouts/ProductToolbar')
);

const GoogleTranslate = React.lazy(
  () => import('@/components/common/GoogleTranslate')
);
const Logos = React.lazy(() => import('@/components/ui/Logos'));
const AuthModal = React.lazy(
  () => import('@/features/auth/components/AuthModal')
);
const ForgotPasswordComponent = React.lazy(
  () => import('@/features/auth/components/ForgotPasswordComponent')
);

export type AuthModalType = 'user' | 'hr';

type NavLink = {
  url: string;
  name: string;
  id?: string;
  target?: string;
  match?: string;
};

function getGwaUrl(): string {
  if (typeof window === 'undefined') {
    return 'https://gwa.zangia.mn';
  }
  const hostname = window.location.hostname;
  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.endsWith('.local')
  ) {
    return '/gwa';
  }
  if (hostname.includes('zangia.mn') && !hostname.includes('zangia.dev')) {
    return 'https://gwa.zangia.mn';
  }
  if (hostname.includes('zangia.dev')) {
    return 'https://gwa.zangia.dev';
  }
  return 'https://gwa.zangia.mn';
}

function isNavLinkActive(pathname: string, link: NavLink): boolean {
  const match = link.match;
  if (!match) return false;
  return pathname === match || pathname.startsWith(`${match}/`);
}

function AuthActionsSkeleton({ size }: { size: 'sm' | 'md' }) {
  if (size === 'sm') {
    return <div className="h-10 w-10 rounded-full bg-gray-200 animate-pulse" />;
  }

  return <div className="h-10 w-30 rounded bg-gray-200 animate-pulse" />;
}

function GuestAuthButtons({
  pathname,
  onOpen,
}: {
  pathname: string;
  onOpen: (type: AuthModalType) => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <Button
        variant="primary"
        className="h-10 w-30 text-sm font-medium"
        onClick={() => {
          if (pathname.startsWith('/account/login')) {
            return;
          }
          onOpen('user');
        }}
      >
        Нэвтрэх
      </Button>
      <Button
        variant="outline"
        className="h-10 text-sm font-medium text-primary border border-primary whitespace-nowrap"
        onClick={() => onOpen('hr')}
      >
        Ажил олгогч
      </Button>
    </div>
  );
}

function NavigationAuthActions({
  isAuthLoading,
  user,
  variant,
  pathname,
  logout,
  onOpenAuth,
}: {
  isAuthLoading: boolean;
  user: UserDTO | undefined;
  variant: 'mobile' | 'desktop';
  pathname: string;
  logout: () => void;
  onOpenAuth: (type: AuthModalType) => void;
}) {
  const skeletonSize = variant === 'mobile' ? 'sm' : 'md';

  if (isAuthLoading) {
    return <AuthActionsSkeleton size={skeletonSize} />;
  }

  if (variant === 'mobile') {
    return (
      <Suspense fallback={<AuthActionsSkeleton size="sm" />}>
        <NavigationDropdownForUser logout={logout} openAuthModal={onOpenAuth} />
      </Suspense>
    );
  }

  if (user) {
    return (
      <Suspense fallback={<AuthActionsSkeleton size="md" />}>
        <div className="flex items-center gap-x-3">
          <UserActionToolbar />
          <NavigationDropdownNotification />
          <NavigationDropdownForUser
            logout={logout}
            openAuthModal={() => onOpenAuth('user')}
          />
        </div>
      </Suspense>
    );
  }

  return <GuestAuthButtons pathname={pathname} onOpen={onOpenAuth} />;
}

export default function Navigation() {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authType, setAuthType] = useState<AuthModalType>('user');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [forgotPasswordModalOpen, setForgotPasswordModalOpen] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  const router = useRouter();
  const { data: user, isAuthLoading } = useAuthMe();
  const { openLoginModal } = useAuthStore((state) => state);
  const pathname = usePathname();
  // GWA-ийн URL hostname-оос хамаарна — server дээр мэдэгдэхгүй тул hydration-ий дараа солино
  const navLinks = useMemo<NavLink[]>(
    () => [
      { url: '/job/list', name: 'Ажлын зар', match: '/job' },
      { url: '/company', name: 'Компани', id: 'company-nav', match: '/company' },
      {
        url: isHydrated ? getGwaUrl() : 'https://gwa.zangia.mn',
        name: 'GWA',
        match: '/gwa',
      },
    ],
    [isHydrated],
  );
  const searchParams = useSearchParams();
  const hrMarketPath = pathname.startsWith('/hr-market');
  const mentorshipPath = pathname.startsWith('/mentorship');
  const showAuthSkeleton = !isHydrated || isAuthLoading;

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    const auth = searchParams.get('auth');
    if (auth === 'register') {
      setAuthType(searchParams.get('type') === 'hr' ? 'hr' : 'user');
      setAuthModalOpen(true);
    }
  }, [searchParams]);

  const logoPath = isChristmasPeriod()
    ? '/images/logos/xmass-logo.svg'
    : '/images/logos/logo.png';

  useEffect(() => {
    if (mobileMenuOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (openLoginModal) setAuthModalOpen(true);
  }, [openLoginModal]);

  // Нэвтрэх шаардсан үйлдлийн дараа нэвтэрсэн бол тэр хуудас руу нь буцаана
  useEffect(() => {
    if (!user) return;
    const pending = takePendingAuthRedirect();
    if (pending && pending !== getCurrentPath()) router.replace(pending);
  }, [user, router]);

  const logout = () => {
    if (process.env.NEXT_PUBLIC_HR_LOGIN === 'true') {
      router.replace(
        `${process.env.NEXT_PUBLIC_APP_ACOUNT_URL}/logout?redirect=${window.location.href}`
      );
    } else {
      logoutSession();
      router.replace('/');
    }
  };

  const openUserModal = (type: AuthModalType) => {
    setAuthType(type);
    setAuthModalOpen(true);
  };
  const closeModal = () => {
    setAuthModalOpen(false);
    setOpenLoginModal(false);
    clearPendingAuthRedirect();
  };
  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  const handleAuthSuccess = () => {
    setAuthModalOpen(false);
    setOpenLoginModal(false);
  };

  return (
    <>
      <header className="bg-white fixed top-0 left-0 right-0 shadow-lg z-50">
        <nav className="container mx-auto flex items-center z-60">
          <div className="lg:hidden flex items-center justify-between w-full p-3">
            <div className="flex items-center gap-x-2">
              <button onClick={toggleMobileMenu} className="focus:outline-none">
                <MenuToggle isOpen={mobileMenuOpen} toggle={toggleMobileMenu} />
              </button>

              <Link href="/" className="flex items-center shrink-0">
                <Image
                  src={logoPath}
                  alt="Zangia.mn"
                  width={121}
                  height={28}
                  priority
                  fetchPriority="high"
                  loading="eager"
                />
              </Link>
            </div>

            <div className="flex items-center gap-x-1 max-w-max">
              <GoogleTranslate />
              <ProductToolbar />
              <NavigationAuthActions
                isAuthLoading={showAuthSkeleton}
                user={user}
                variant="mobile"
                pathname={pathname}
                logout={logout}
                onOpenAuth={openUserModal}
              />
            </div>
          </div>

          <div className="hidden lg:flex items-center justify-between w-full">
            <div className="flex items-center gap-x-16">
              <Link href="/" className="shrink-0 flex items-center">
                <Image
                  src={logoPath}
                  alt="Zangia.mn"
                  width={162}
                  height={38}
                  priority
                  fetchPriority="high"
                  loading="eager"
                  className="w-auto h-9"
                />
              </Link>

              {!mobileMenuOpen && (
                <div className="flex items-center gap-12">
                  <ul className="flex items-center xl:gap-4 lg:gap-4 text-base text-primary">
                    {navLinks.map((link) => {
                      const active = isNavLinkActive(pathname, link);
                      return (
                        <li key={link.url}>
                          <Link
                            id={link.id}
                            href={link.url}
                            aria-current={active ? 'page' : undefined}
                            className={cn(
                              'relative py-3 sm:py-5 flex items-center text-nowrap',
                              active && 'font-semibold'
                            )}
                            target={link.target}
                            rel={
                              link.target === '_blank'
                                ? 'noopener noreferrer'
                                : undefined
                            }
                          >
                            {link.name}
                            <span
                              className={cn(
                                'absolute bottom-0 left-0 right-0 h-1 rounded-t-full bg-primary transition-all duration-300 ease-in-out',
                                active
                                  ? 'opacity-100 scale-x-100'
                                  : 'opacity-0 scale-x-0'
                              )}
                            />
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                  <Logos />
                </div>
              )}
            </div>

            <div className="flex items-center gap-x-3">
              <Suspense fallback={null}>
                <GoogleTranslate />
              </Suspense>
              <div className="bg-gray-300 rotate-90 h-px rounded-lg w-6" />
              <Suspense fallback={null}>
                <ProductToolbar />
              </Suspense>
              <NavigationAuthActions
                isAuthLoading={showAuthSkeleton}
                user={user}
                variant="desktop"
                pathname={pathname}
                logout={logout}
                onOpenAuth={openUserModal}
              />
            </div>
          </div>
        </nav>

        <AnimatePresence>
          {mobileMenuOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.5 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="fixed inset-0 z-40"
                onClick={toggleMobileMenu}
              />

              <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="fixed inset-0 z-50 bg-white lg:hidden mt-16 w-4/5 shadow-lg"
              >
                <ul className="py-1 text-primary font-medium text-sm">
                  {navLinks.map((link) => {
                    const active = isNavLinkActive(pathname, link);
                    return (
                      <li key={link.url}>
                        <Link
                          id={link.id}
                          href={link.url}
                          aria-current={active ? 'page' : undefined}
                          className={cn(
                            'p-4 flex items-center border-b border-black/10 justify-between',
                            active && 'bg-primary-50 font-semibold'
                          )}
                          onClick={toggleMobileMenu}
                          target={link.target}
                          rel={
                            link.target === '_blank'
                              ? 'noopener noreferrer'
                              : undefined
                          }
                        >
                          {link.name}
                          <ChevronRight size="16" className="text-primary" />
                        </Link>
                      </li>
                    );
                  })}
                  {ZANGIA_LOGOS.map((logo) => (
                    <li key={`${logo.src}-${logo.alt}`}>
                      <a
                        href={logo.href}
                        className="p-4 flex items-center border-b border-black/10 justify-between"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Image
                          src={logo.src}
                          width={146}
                          height={21}
                          alt={logo.alt}
                          priority
                          fetchPriority="high"
                          loading="eager"
                          className="h-5 w-auto"
                        />
                        <ChevronRight size="16" className="text-primary" />
                      </a>
                    </li>
                  ))}
                </ul>
                <div className="fixed bottom-0 pb-10 text-center w-4/5 font-bold text-sm">
                  <p>@2025</p>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {!user && (
          <>
            <ModalWrapper authModalOpen={authModalOpen} closeModal={closeModal}>
              <div className="mx-auto w-full">
                <Suspense fallback={null}>
                  <AuthModal
                    authType={authType}
                    onSuccessAuthModal={handleAuthSuccess}
                    skipHrRedirect={pathname.startsWith('/salary-summary')}
                    setForgotPasswordModalOpen={() =>
                      setForgotPasswordModalOpen(true)
                    }
                    initialTab={
                      searchParams.get('auth') === 'register'
                        ? 'register'
                        : 'login'
                    }
                    initialStep={
                      Math.min(
                        3,
                        Math.max(
                          1,
                          parseInt(searchParams.get('step') ?? '1', 10)
                        )
                      ) as 1 | 2 | 3
                    }
                    emongoliaToken={searchParams.get('token')}
                  />
                </Suspense>
              </div>
            </ModalWrapper>

            <Modal
              isOpen={forgotPasswordModalOpen}
              setIsOpen={setForgotPasswordModalOpen}
              title={''}
              footer={null}
              header={false}
            >
              <Suspense fallback={null}>
                <ForgotPasswordComponent
                  onClose={() => setForgotPasswordModalOpen(false)}
                />
              </Suspense>
            </Modal>
          </>
        )}
      </header>
      {!mentorshipPath && (
        <div
          className={
            hrMarketPath
              ? `h-16 md:h-17 lg:h-21 xl:h-16 content-normal`
              : `h-20 md:h-20 content-normal`
          }
        />
      )}
    </>
  );
}
